import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  actionStatusAccent,
  ActionStatusIcon,
  normalizeActionStatus,
} from "common/components/actionStatus";
import ActionEvidenceList from "features/programmesActions/ActionEvidenceList";
import ActionEvidenceUpload from "features/programmesActions/ActionEvidenceUpload";
import { getFormattedCategoryName } from "features/programmesActions/ActionList";
import UnifiedEvidenceList from "features/programmesActions/UnifiedEvidenceList";
import { useNotification } from "app/NotificationProvider";
import type { CorporateSettableStatus } from "model";
import { getEvidenceType, useUpdateSubmittedActionStatus } from "model";
import type { FarmerActionData } from "../hooks/useInsightsDashboardQueries";

const STATUS_OPTIONS: { value: CorporateSettableStatus; label: string }[] = [
  { value: "submitted", label: "Submitted" },
  { value: "on-track", label: "On track" },
  { value: "completed", label: "Completed" },
];

interface Props {
  action: FarmerActionData;
  farmerGuid?: string;
}

export default function ActionDetailView({ action, farmerGuid }: Props) {
  const categoryId = parseInt(action["SubmittedActions.categoryId"] ?? "0", 10);
  const category =
    getFormattedCategoryName(categoryId) ||
    action["SubmittedActions.category"] ||
    "N/A";
  const activity = action["SubmittedActions.action"] || "N/A";
  const submittedDetails = action["SubmittedActions.submittedDetails"];
  const detailQuestion = action["SubmittedActions.detailQuestion"];
  const actionId = action["SubmittedActions.actionId"] || "";
  const guid = action["SubmittedActions.guid"];
  const requiredEvidence = action["SubmittedActions.requiredEvidence"] || "";
  const evidenceType = getEvidenceType(requiredEvidence);
  const showUnifiedEvidence = farmerGuid && evidenceType;
  const status = normalizeActionStatus(action["SubmittedActions.status"]);
  const accent = actionStatusAccent(status);

  const { notify } = useNotification();
  const updateStatus = useUpdateSubmittedActionStatus();

  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const statusLabel =
    STATUS_OPTIONS.find((option) => option.value === status)?.label ??
    "Unknown";

  const handleSetStatus = (next: CorporateSettableStatus) => {
    setStatusAnchor(null);
    if (!guid || next === status || updateStatus.isPending) return;
    updateStatus.mutate(
      { guid, status: next },
      {
        onSuccess: () =>
          notify(
            `Action status updated to ${
              STATUS_OPTIONS.find((option) => option.value === next)?.label
            }`,
            "success"
          ),
        onError: () => notify("Failed to update action status", "error"),
      }
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
      }}
    >
      {/* Left Section - Action Details */}
      <Box
        sx={{
          flex: { xs: "1 1 auto", md: "0 0 379px" },
          border: "1px solid #e4e4e7",
          borderLeft: accent ? `4px solid ${accent}` : "1px solid #e4e4e7",
          borderRadius: "8px",
          p: 3,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mb: 0.75,
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontSize: "24px", fontWeight: 600, color: "#09090b" }}
            >
              Action Details
            </Typography>
            <Button
              size="small"
              aria-label={`Action status: ${statusLabel}`}
              aria-haspopup="menu"
              aria-controls={statusAnchor ? "action-status-menu" : undefined}
              aria-expanded={Boolean(statusAnchor)}
              disabled={!guid || updateStatus.isPending}
              onClick={(event) => setStatusAnchor(event.currentTarget)}
              startIcon={
                updateStatus.isPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <ActionStatusIcon status={status} size={16} />
                )
              }
              endIcon={<ExpandMoreIcon />}
              sx={{
                borderRadius: "999px",
                px: 1.25,
                py: 0.5,
                minWidth: 0,
                flexShrink: 0,
                fontSize: "0.8rem",
                fontWeight: 600,
                lineHeight: 1.2,
                textTransform: "none",
                color: accent ?? "#52525b",
                bgcolor:
                  status === "completed"
                    ? "#dcfce7"
                    : status === "on-track"
                    ? "#e0f2fe"
                    : "#f4f4f5",
                "&:hover": {
                  bgcolor:
                    status === "completed"
                      ? "#bbf7d0"
                      : status === "on-track"
                      ? "#bae6fd"
                      : "#e4e4e7",
                },
                "& .MuiButton-startIcon": { mr: 0.5 },
                "& .MuiButton-endIcon": { ml: 0.5 },
              }}
            >
              {statusLabel}
            </Button>
            <Menu
              id="action-status-menu"
              anchorEl={statusAnchor}
              open={Boolean(statusAnchor)}
              onClose={() => setStatusAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{ list: { "aria-label": "Action status" } }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={status === option.value}
                  disabled={updateStatus.isPending}
                  onClick={() => handleSetStatus(option.value)}
                  sx={{ gap: 1, fontSize: "0.875rem" }}
                >
                  <ActionStatusIcon status={option.value} size={16} />
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "#71717a", fontSize: "14px" }}
          >
            Details about the action submitted by the farmer
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Category */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 1,
                color: "#18181b",
                fontSize: "14px",
              }}
            >
              Category
            </Typography>
            <Box
              sx={{
                bgcolor: "#eaeaea",
                border: "1px solid #e4e4e7",
                borderRadius: "6px",
                minHeight: "40px",
                display: "flex",
                alignItems: "center",
                px: 1.5,
                py: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "#18181b",
                  fontSize: "14px",
                }}
              >
                {category}
              </Typography>
            </Box>
          </Box>

          {/* Activity */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 1,
                color: "#18181b",
                fontSize: "14px",
              }}
            >
              Activity
            </Typography>
            <Box
              sx={{
                bgcolor: "#eaeaea",
                border: "1px solid #e4e4e7",
                borderRadius: "6px",
                minHeight: "80px",
                p: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#18181b",
                  fontSize: "14px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {activity}
              </Typography>
            </Box>
          </Box>

          {/* Farmer's Notes */}
          {submittedDetails && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "#18181b",
                    fontSize: "14px",
                  }}
                >
                  Farmer&apos;s notes
                </Typography>
                {detailQuestion && (
                  <Tooltip
                    title={
                      <Typography component="span">
                        This is the farmer&apos;s answer to the question:{" "}
                        <Typography
                          component="span"
                          sx={{ fontStyle: "italic", fontSize: "14px" }}
                        >
                          {detailQuestion}
                        </Typography>
                      </Typography>
                    }
                    arrow
                  >
                    <InfoIcon
                      sx={{
                        fontSize: "16px",
                        color: "#71717a",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Box
                sx={{
                  bgcolor: "#eaeaea",
                  border: "1px solid #e4e4e7",
                  borderRadius: "6px",
                  minHeight: "80px",
                  p: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#18181b",
                    fontSize: "14px",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {submittedDetails}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Section - Documentation */}
      <Box
        sx={{
          flex: "1 1 auto",
          border: "1px solid #e4e4e7",
          borderRadius: "8px",
          p: 3,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AssignmentTurnedInIcon sx={{ color: "#16a34a", fontSize: 28 }} />
            <Typography
              variant="h4"
              sx={{
                fontSize: "24px",
                fontWeight: 600,
                mb: 0.75,
                color: "#09090b",
              }}
            >
              Documentation
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "#71717a", fontSize: "14px", mt: 1 }}
          >
            Upload photos and other evidence for this action.
          </Typography>
        </Box>
        {guid && farmerGuid && showUnifiedEvidence ? (
          <UnifiedEvidenceList
            evidenceType={evidenceType}
            actionSubmissionGuid={guid}
            requiredEvidence={requiredEvidence}
            userGuid={farmerGuid}
            readOnly
          />
        ) : (
          guid && (
            <>
              <Box sx={{ mb: 2 }}>
                <ActionEvidenceUpload
                  actionSubmissionGuid={guid}
                  requiredEvidence={requiredEvidence}
                />
              </Box>
              <ActionEvidenceList actionSubmissionGuid={guid} />
            </>
          )
        )}
      </Box>
    </Box>
  );
}
